import type zh from '../zh/chat';

/** Chat panel, English. Typed against the zh file so a missing key fails tsc. */
const chat: typeof zh = {
  /** First assistant message of a fresh session, per agent (AgentContext). */
  welcome: {
    rocky: "Hi! 👋 I'm Rocky. How can I help?",
    marketing_agent: "Hi! 👋 I'm the Marketing Agent. How can I help?",
    campaign_copilot_agent: "Hi! 👋 I'm the Campaign Copilot. How can I help?",
    eval: "Hi! 👋 I'm the Evaluation Agent. I can look up evaluation runs, failed cases and quality trends. How can I help?",
  },
  header: {
    title: 'Chat',
    historyTitle: 'Chat History',
    history: 'Chat history',
    backToChat: 'Back to chat',
    newConversation: 'New conversation',
    enterFullscreen: 'Enter fullscreen',
    exitFullscreen: 'Exit fullscreen',
    close: 'Close chat',
    open: 'Open chat',
  },
  thought: {
    thinking: 'Thinking...',
    completed: 'Thinking completed',
    hint: 'For accurate data this may take a while. Meanwhile you can click <icon /> at the top of the chat to start a new conversation; this task keeps running in the background.',
    newChatAlt: 'New conversation',
  },
  /** Follow-up suggestions under an answer (choice_group heading / group name). */
  followUps: {
    title: 'Next, you could',
  },
  suggestions: {
    title: 'Suggested Questions',
  },
  disclaimer: 'Rocky is AI and can make mistakes.',
  messages: {
    loadingOlder: 'Loading older messages...',
    empty: 'This conversation has no messages yet.',
    unknownError: 'Unknown error',
    userAvatar: 'Avatar of {{name}}',
    userFallback: 'User',
    assistantAvatar: 'Avatar of Rocky AI assistant',
    genericAssistantAvatar: 'Avatar of AI assistant',
    noResponse: '(No response)',
    stopped: '(Stopped)',
    scrollToLatest: 'Scroll to latest message',
  },
  errors: {
    auth: 'Authentication error. Please refresh the page to re-login.',
    withMessage: 'Error: {{message}}',
    noResponse: 'No response',
    sendFailed: 'Sorry, the message could not be sent. Please try again.',
  },
  session: {
    switchConfirm: 'This conversation is still connecting. Switching may lose the reply. Switch anyway?',
  },
  input: {
    model: 'Model',
    placeholder: 'How can I assist you today? Send a message to start.',
    readOnly: 'Read-only mode (Admin View)',
    emailFallback: "Don't wait for me. Email me the report and analysis when it's done.",
    willQueue: 'Your question will be added to the queue…',
    gateBlocks: 'Respond to the approval card above first (approve / cancel)',
    gateReply: 'Reply to the card above: changes or questions (use the card\'s button to approve)',
    stop: 'Stop generating',
    stopKeepQueue: 'Stop current response (queued questions will continue)',
    context: 'Context: {{percent}}%',
    send: 'Send message',
    addToQueue: 'Add to pending queue',
  },
  queue: {
    gateFirst: 'Respond to the approval card above first',
    full: 'The queue holds at most {{max}} questions',
    label: 'Pending questions queue',
    remove: 'Remove from queue',
    removeNamed: 'Remove queued question: {{text}}',
    sendNow: 'Send now (stops current response)',
    sendNowNamed: 'Send queued question now: {{text}}',
  },
  files: {
    attach: 'Attach files',
    attachLabel: 'Attach Files',
    drop: 'Drop files to upload',
    uploading: 'Uploading...',
    remove: 'Remove file',
    removeNamed: 'Remove {{name}}',
    unsupported: '{{exts}} files are not supported. Convert them to a supported format such as PDF, CSV or TXT and try again.',
    listSeparator: ', ',
    openInWorkspace: 'Open in workspace',
  },
  contextFile: {
    banner: 'Chatting about <name>{{fileName}}</name>',
    stopNamed: 'Stop using {{fileName}} as context',
    stop: 'Stop using this file as chat context',
    chipExcludeTitle: '{{name}} (click to exclude from context)',
    chipIncludeTitle: '{{name}} (click to include in context)',
    chipExclude: 'Exclude {{name}} from context',
    chipInclude: 'Include {{name}} in context',
  },
  scope: {
    kb: 'KB: {{name}}',
    assetTitle: 'Chat is scoped to asset "{{name}}". Click to exit.',
    assetExit: 'Exit Asset KB scope: {{name}}',
    useCaseTitle: 'Chat is scoped to use case "{{name}}". Click to exit.',
    useCaseExit: 'Exit Use Case KB scope: {{name}}',
    storylineTitle: 'Chat is scoped to storyline "{{name}}". Click to exit.',
    storylineExit: 'Exit Storyline KB scope: {{name}}',
  },
  pack: {
    label: "Rocky's Pack",
    agentLabel: 'Skill Pack',
    open: 'Open {{label}} skills',
    unavailable: '{{label}} (unavailable while loading)',
    descriptions: {
      DataRetriever:
        'Fetches revenue, usage, pipeline, and account data instantly - no more digging through dashboards or waiting for report pulls.',
      Hedwig:
        'Intelligence briefs for field teams — fetching industrial data and resources to deliver industry signals, competitor moves, and customer insights that drive actions.',
      Cheetah:
        'Cheetah provides internal expert booking service — match the right person to your needs, one-click booking and track the entire engagement workflow.',
      IVT: 'Industry vertical solutions — explore solution maps, storylines, assets and motions to power vertical selling.',
      CampaignCopilot:
        'Campaign orchestration — audience segmentation, personalized emails, Octopus program creation and performance analytics.',
      EventInsights: 'Event insights — pre- and post-event data reports.',
    },
  },
  markdown: {
    code: 'Code',
    copy: 'Copy',
    copied: 'Copied',
  },
  history: {
    loading: 'Loading sessions...',
    empty: 'No chat history yet',
    untitled: 'Untitled conversation',
    loadMore: 'Load more',
    loadingMore: 'Loading...',
    loadFailed: 'Failed to load sessions',
    loadMoreFailed: 'Failed to load more sessions',
    loadConversationFailed: 'Failed to load conversation',
    justNow: 'just now',
    minutesAgo: '{{n}}m ago',
    hoursAgo: '{{n}}h ago',
    daysAgo: '{{n}}d ago',
  },
};

export default chat;
